import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  MAX_ATTEMPTS,
  WORD_LENGTH,
  evaluateGuess,
  isSolved,
  mergeKeyboardStates,
  type GuessEntry,
  type LetterState,
} from "@mk/shared";
import { loadWordLists, pickPracticeAnswer, type WordLists } from "../../lib/words.ts";
import { useSession } from "../../lib/session.ts";
import type { DraftCells } from "../boardDraft.ts";
import { useBoardInput } from "../useBoardInput.ts";
import { savePracticeRun } from "./practiceHistory.ts";
import { messages } from "../../i18n/index.ts";
import { playSound } from "../../lib/sound.ts";

export type SoloStatus = "loading" | "error" | "playing" | "won" | "lost";

export interface SoloGame {
  status: SoloStatus;
  answer: string;
  rows: GuessEntry[];
  cells: DraftCells;
  pinned: number | null;
  selectCell: (index: number) => void;
  keyboard: Record<string, LetterState>;
  toast: string | null;
  shake: boolean;
  /** Kutucuk çevirme animasyonu bitene kadar sonuç ekranı beklesin. */
  revealed: boolean;
  press: (key: string) => void;
  newGame: () => void;
}

const REVEAL_MS = WORD_LENGTH * 180 + 520;

export function useSoloGame(): SoloGame {
  const [lists, setLists] = useState<WordLists | null>(null);
  const [status, setStatus] = useState<SoloStatus>("loading");
  const [answer, setAnswer] = useState("");
  const [rows, setRows] = useState<GuessEntry[]>([]);
  const [toast, setToast] = useState<string | null>(null);
  const [shake, setShake] = useState(false);
  const [revealed, setRevealed] = useState(true);

  const toastTimer = useRef<number | undefined>(undefined);
  const shakeTimer = useRef<number | undefined>(undefined);
  const revealTimer = useRef<number | undefined>(undefined);
  const submittingRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    loadWordLists()
      .then((loaded) => {
        if (cancelled) return;
        setLists(loaded);
        setAnswer(pickPracticeAnswer(loaded, useSession.getState().user?.id ?? null));
        setStatus("playing");
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(
    () => () => {
      window.clearTimeout(toastTimer.current);
      window.clearTimeout(shakeTimer.current);
      window.clearTimeout(revealTimer.current);
    },
    [],
  );

  const warn = useCallback((message: string) => {
    setToast(message);
    setShake(true);
    window.clearTimeout(toastTimer.current);
    window.clearTimeout(shakeTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 1600);
    shakeTimer.current = window.setTimeout(() => setShake(false), 440);
  }, []);

  const submit = useCallback(
    (word: string | null) => {
      if (!lists || submittingRef.current) return;

      if (word === null) {
        warn(messages.feedback.lengthRequired(WORD_LENGTH));
        return;
      }
      if (!lists.valid.has(word)) {
        warn(messages.feedback.notInList);
        return;
      }

      submittingRef.current = true;
      const pattern = evaluateGuess(word, answer);
      const nextRows = [...rows, { word, pattern }];
      setRows(nextRows);

      const won = isSolved(pattern);
      const lost = !won && nextRows.length >= MAX_ATTEMPTS;
      if (won || lost) {
        if (won) playSound("solved");
        // Kayıt burada, durum güncelleyicisinin dışında: StrictMode
        // güncelleyicileri iki kez çağırıyor, bu yan etki bir kez olmalı.
        savePracticeRun({ answer, solved: won, guesses: nextRows });
        setStatus(won ? "won" : "lost");
        setRevealed(false);
        window.clearTimeout(revealTimer.current);
        revealTimer.current = window.setTimeout(() => setRevealed(true), REVEAL_MS);
      }
    },
    [answer, lists, rows, warn],
  );

  const input = useBoardInput({ disabled: status !== "playing", onSubmit: submit });

  // Tahmin kabul edildiğinde aktif satır boşalsın.
  const rowCount = rows.length;
  const clearDraft = input.clear;
  useEffect(() => {
    clearDraft();
    submittingRef.current = false;
  }, [rowCount, clearDraft]);

  const newGame = useCallback(() => {
    if (!lists) return;
    window.clearTimeout(revealTimer.current);
    setAnswer((previous) => pickPracticeAnswer(lists, useSession.getState().user?.id ?? null, previous));
    setRows([]);
    submittingRef.current = false;
    clearDraft();
    setToast(null);
    setShake(false);
    setRevealed(true);
    setStatus("playing");
  }, [clearDraft, lists]);

  const keyboard = useMemo(
    () =>
      rows.reduce<Record<string, LetterState>>(
        (acc, row) => mergeKeyboardStates(acc, row.word, row.pattern),
        {},
      ),
    [rows],
  );

  return {
    status,
    answer,
    rows,
    cells: input.cells,
    pinned: input.pinned,
    selectCell: input.selectCell,
    keyboard,
    toast,
    shake,
    revealed,
    press: input.press,
    newGame,
  };
}
